import { useState, useEffect, useRef } from "react";
import { Zap, CalendarDays, UserPlus } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { STATUS_COLUMNS, STATUS_LABELS, TASK_STATUS_ICON } from "./constants";

export const TaskListRow = ({ task, teamMembers, onStatusChange, onUpdate }) => {
  const [showAssignee, setShowAssignee] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const assigneeRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (assigneeRef.current && !assigneeRef.current.contains(e.target)) setShowAssignee(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="flex items-center gap-3 bg-white border border-border/50 rounded-xl px-4 py-3" data-testid={`task-row-${task.id}`}>
      {TASK_STATUS_ICON[task.status] || TASK_STATUS_ICON.todo}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{task.title}</p>
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-foreground/50">
          {task.stage && <span className="px-1.5 py-0.5 bg-foreground/[0.04] rounded capitalize">{task.stage}</span>}
          {task.automation_type === "automated" && <span className="text-amber-500"><Zap className="w-2.5 h-2.5 inline" /> Auto</span>}
        </div>
      </div>
      <Popover open={showDate} onOpenChange={setShowDate}>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-1 text-[10px] text-foreground/50 hover:text-foreground/60 px-2 py-1 rounded-lg hover:bg-foreground/5" data-testid={`task-list-due-${task.id}`}>
            <CalendarDays className="w-3 h-3" />
            {task.due_date ? new Date(task.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Set date"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[100]" align="end">
          <Calendar mode="single" selected={task.due_date ? new Date(task.due_date) : undefined}
            onSelect={(date) => { if (date) onUpdate(task.id, { due_date: date.toISOString().slice(0, 10) }); setShowDate(false); }} />
        </PopoverContent>
      </Popover>
      <div className="relative" ref={assigneeRef}>
        <button onClick={() => setShowAssignee(p => !p)} data-testid={`task-list-assignee-${task.id}`} title={task.assignee?.name || "Assign"}>
          {task.assignee ? (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ background: task.assignee.color || "#999" }}>{task.assignee.initials}</div>
          ) : (
            <div className="w-6 h-6 rounded-full border border-dashed border-foreground/20 flex items-center justify-center"><UserPlus className="w-3 h-3 text-foreground/45" /></div>
          )}
        </button>
        {showAssignee && teamMembers?.length > 0 && (
          <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-elevated border border-border/60 overflow-hidden z-50" style={{ animation: "cardFadeIn 0.1s ease-out" }}>
            <div className="px-3 py-2 border-b border-border/30"><p className="text-[9px] font-bold text-foreground/45 uppercase tracking-wider">Assign to</p></div>
            {teamMembers?.map((m, i) => (
              <button key={i} onClick={() => { onUpdate(task.id, { assignee: { name: m.name, initials: m.initials, color: m.color } }); setShowAssignee(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-foreground/[0.03] text-left">
                <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[6px] font-bold" style={{ background: m.color }}>{m.initials}</div>
                <span className="text-[11px] font-medium text-foreground/60">{m.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <select value={task.status} onChange={e => onStatusChange(task.id, e.target.value)}
        className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-border/40 outline-none capitalize text-foreground/50"
        data-testid={`task-status-${task.id}`}>
        {STATUS_COLUMNS?.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        <option value="blocked">Blocked</option>
      </select>
    </div>
  );
};
